import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, RefreshCw, TerminalSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";

/* Admin-only diagnostics for the embedded face engine: runner state, whether
   the pre-built engine bundle is present, and the tail of the engine log.
   Lets an outage be diagnosed from the UI (and pasted to support) without
   shell access to the production host. Read-only. */
export const FaceEngineLogPanel = () => {
  const { user } = useAuth();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["face-engine-log"],
    queryFn: async () => (await api.get("/admin/face-engine-log", { params: { lines: 200 } })).data,
    enabled: isAdmin && open,
  });
  if (!isAdmin) return null;
  return (
    <div data-testid="face-engine-log-panel" className="rounded-xl border border-border bg-card">
      <button type="button" data-testid="face-engine-log-toggle"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm font-semibold hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl">
        <span className="flex items-center gap-2">
          <TerminalSquare className="h-4 w-4 text-primary" /> {t("engineLogTitle")}
        </span>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && (
        <div className="space-y-2 border-t border-border p-4">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
            <div className="space-y-0.5">
              <p data-testid="face-engine-runner-state">
                <span className="text-muted-foreground">{t("engineRunnerState")}:</span>{" "}
                <span className="font-mono">{data?.runnerState ?? "…"}</span>
              </p>
              <p data-testid="face-engine-bundle-state">
                <span className="text-muted-foreground">{t("engineBundle")}:</span>{" "}
                <span className={data?.bundleAvailable ? "font-medium text-emerald-700" : "font-medium text-rose-700"}>
                  {data ? (data.bundleAvailable ? t("engineBundlePresent") : t("engineBundleMissing")) : "…"}
                </span>
              </p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => refetch()} disabled={isFetching}
              data-testid="face-engine-log-refresh">
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} /> {t("refreshHealth")}
            </Button>
          </div>
          <pre data-testid="face-engine-log-output"
            className="max-h-72 overflow-auto rounded-lg bg-secondary p-3 font-mono text-[11px] leading-relaxed text-foreground whitespace-pre-wrap break-words">
            {data?.log ?? "…"}
          </pre>
          <p className="text-xs text-muted-foreground">{t("engineLogHint")}</p>
        </div>
      )}
    </div>
  );
};
